import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { ArchiveRestore, RotateCcw, Trash2 } from "lucide-react";
import {
  listRecycleBinEntries,
  permanentlyDeleteRecycleBinEntry,
  restoreRecycleBinEntry,
} from "../../utils/recycleBinService";

const TYPE_OPTIONS = [
  { id: "all", label: "All" },
  { id: "account", label: "Accounts" },
  { id: "task", label: "Tasks" },
  { id: "inventory", label: "Inventory" },
];

export default function AdminRecycleBinTab({ companyId }) {
  const db = getFirestore();
  const auth = getAuth();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [entries, setEntries] = useState([]);

  const loadEntries = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const next = await listRecycleBinEntries(db, companyId);
      setEntries(Array.isArray(next) ? next : []);
    } catch (err) {
      console.error("[AdminRecycleBinTab] load failed:", err);
      setError(err?.message || "Failed to load recycle bin.");
    } finally {
      setLoading(false);
    }
  }, [companyId, db]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const filteredEntries = useMemo(() => {
    return (entries || []).filter((entry) => {
      if (entry.status !== "deleted") return false;
      if (typeFilter === "all") return true;
      return entry.entityType === typeFilter;
    });
  }, [entries, typeFilter]);

  const handleRestore = async (entryId) => {
    if (!entryId || !companyId) return;
    setSavingId(entryId);
    setError("");
    setStatus("");
    try {
      const currentUser = auth.currentUser;
      await restoreRecycleBinEntry({
        db,
        companyId,
        recycleEntryId: entryId,
        restoredByUid: currentUser?.uid || null,
      });
      setStatus("Item restored from recycle bin.");
      await loadEntries();
    } catch (err) {
      console.error("[AdminRecycleBinTab] restore failed:", err);
      setError(err?.message || "Failed to restore item.");
    } finally {
      setSavingId("");
    }
  };

  const handlePermanentDelete = async (entryId, label) => {
    if (!entryId || !companyId) return;
    const ok = window.confirm(`Permanently delete ${label || "this item"} from the recycle bin?`);
    if (!ok) return;

    setSavingId(entryId);
    setError("");
    setStatus("");
    try {
      await permanentlyDeleteRecycleBinEntry({ db, companyId, recycleEntryId: entryId });
      setStatus("Recycle bin entry permanently deleted.");
      await loadEntries();
    } catch (err) {
      console.error("[AdminRecycleBinTab] permanent delete failed:", err);
      setError(err?.message || "Failed to permanently delete item.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-slate-900 font-semibold">
              <ArchiveRestore className="h-4 w-4 text-blue-600" />
              Recycle Bin
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Deleted accounts, tasks, and inventory items land here first so they can be restored.
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={loadEntries}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {status ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {status}
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="px-4 py-8 text-sm text-slate-500">Loading recycle bin…</div>
        ) : filteredEntries.length === 0 ? (
          <div className="px-4 py-8 text-sm text-slate-500">No deleted items waiting in the recycle bin.</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredEntries.map((entry) => {
              const disabled = savingId === entry.id;
              return (
                <div key={entry.id} className="px-4 py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {entry.displayName || entry.entityId}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                        {entry.entityLabel || entry.entityType}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      ID: {entry.entityId}
                      {entry.deletedAt?.toDate ? ` • Deleted ${entry.deletedAt.toDate().toLocaleString()}` : ""}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRestore(entry.id)}
                      disabled={disabled}
                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Restore
                    </button>

                    <button
                      type="button"
                      onClick={() => handlePermanentDelete(entry.id, entry.displayName || entry.entityId)}
                      disabled={disabled}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Forever
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
